import React, { useState, FormEvent, useMemo, useCallback, useEffect } from 'react';
import { Alert, Button, FormGroup, Label, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import styled from '../themes/themed-styled-components';
import { CenteredContainer } from '.';
import { useLocation, Redirect } from 'react-router';
import { HacpackMutation, HacpackMutationSignInArguments, SignInResult, User } from '../types/graph';
import { useMutation, gql } from 'urql';
import { useCurrentUser } from '../hooks';
import { filter, isNil } from 'lodash';
import { Link } from 'react-router-dom';
import { GraphType, Override, PickGraphType } from '../types';

type SignInMutationData = GraphType<HacpackMutation> & {
    signIn: Override<SignInResult, {
        data: PickGraphType<User, 'id'>
    }>
};

type SignInMutationArguments = HacpackMutationSignInArguments;

const signInMutation = gql<SignInMutationData, SignInMutationArguments>`mutation ($credentials: SignInInput!) {
    signIn (credentials: $credentials)  {
        isOk
        data {
            id
        }
    }
}`;

const SignInFormWrap = styled.div`
    
`;

const SignInForm = () => {
    const { currentUser, fetchingCurrentUser, reloadCurrentUser } = useCurrentUser();

    const [signInResult, signIn] = useMutation<SignInMutationData, SignInMutationArguments>(signInMutation);

    const { data, fetching: fetchingApproval, error } = signInResult;

    const { t } = useTranslation('common');
    const { state } = useLocation<{ from?: string } | undefined>();

    const [loginOrEmail, setLoginOrEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);

    const from = state?.from;
    const credentials = useMemo(() => ({ loginOrEmail, password, rememberMe }), [loginOrEmail, password, rememberMe]);
    const loginFailed = fetchingApproval === false && data?.signIn.isOk === false;

    useEffect(() => {
        if (data?.signIn.isOk === true) {
            reloadCurrentUser();
        }
    }, [data, reloadCurrentUser]);

    const redirect = useMemo(() => {

        if (currentUser === null) {
            return null;
        }

        if (!isNil(from)) {
            return from;
        }

        if (fetchingCurrentUser) {
            return null;
        }

        const terminals = filter(currentUser.terminals, 'active');

        if (terminals.length === 1) {
            return `/${terminals[0].slug ?? ''}`;
        }

        return '/';
    }, [currentUser, from, fetchingCurrentUser])

    const handleSubmit = useCallback((e: FormEvent) => {
        e.preventDefault();
        signIn({ credentials });
    }, [signIn, credentials]);

    const valid = useMemo(() => loginOrEmail.length > 0 && password.length > 0, [loginOrEmail, password]);

    const inputDisabled = fetchingApproval && fetchingCurrentUser;
    const submitDisabled = inputDisabled || !valid;

    if (error !== undefined) {
        console.error(error);
    }

    if (redirect) {
        return <Redirect to={redirect} />
    }

    return (
        <CenteredContainer>
            <SignInFormWrap>
                {loginFailed === true ? <Alert color="warning">{t('access.loginFailed')}</Alert> : null}
                <form onSubmit={handleSubmit}>
                    <FormGroup disabled={inputDisabled}>
                        <Label>{t('access.usernameOrEmail')}</Label>
                        <Input autoFocus type="text" value={loginOrEmail} onChange={e => setLoginOrEmail(e.target.value)} autoComplete="on" />
                    </FormGroup>
                    <FormGroup disabled={inputDisabled}>
                        <Label>{t('access.password')}</Label>
                        <Input className="form-control" value={password} onChange={e => setPassword(e.target.value)} type="password" autoComplete="on" />
                    </FormGroup>
                    <FormGroup check disabled={inputDisabled} >
                        <Label check>
                            <Input type="checkbox" autoComplete="on" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} /> {t('access.rememberMe')}
                        </Label>
                    </FormGroup>
                    <Button block disabled={submitDisabled} type="submit">
                        {t('access.loginAction')}
                    </Button>
                    <Button tag={Link} to="/recupero-password" block color="link" type="button">
                        {t('Password dimenticata?')}
                    </Button>
                </form>
            </SignInFormWrap>
        </CenteredContainer>
    );
}

export default SignInForm;